import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Autocomplete,
  CircularProgress,
  Checkbox,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { format } from 'date-fns';
import {
  searchProducts,
  searchBundles,
  searchVariants,
  searchModifiers,
  searchOptionValues
} from '../../../api/products';
import type {
  ProductSearchResult,
  BundleSearchResult,
  VariantSearchResult,
  ModifierSearchResult,
  OptionValueSearchResult
} from '../../../api/products';
import type { BulkItemsEditorProps } from './types';

type SearchResult = ProductSearchResult | BundleSearchResult | VariantSearchResult | ModifierSearchResult | OptionValueSearchResult;

export const BulkItemsEditor: React.FC<BulkItemsEditorProps> = ({
  itemsOperation,
  onOperationChange,
  selectedItems,
  onSelectedItemsChange,
  itemType,
  onItemTypeChange,
  itemReason,
  onItemReasonChange,
  itemExpiry,
  onItemExpiryChange,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedQuickOption, setSelectedQuickOption] = useState<string>('endOfDay');

  const getDefault4AM = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(4, 0, 0, 0);
    return tomorrow;
  };

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        performSearch(searchQuery);
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, itemType]);

  const performSearch = async (query: string) => {
    setIsSearching(true);
    try {
      let results: SearchResult[] = [];

      switch (itemType) {
        case 'PRODUCT':
          results = await searchProducts(query);
          break;
        case 'BUNDLE':
          results = await searchBundles(query);
          break;
        case 'VARIANT':
          results = await searchVariants(query);
          break;
        case 'MODIFIER':
          results = await searchModifiers(query);
          break;
        case 'OPTION_VALUE':
          results = await searchOptionValues(query);
          break;
      }

      setSearchResults(results);
    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Manage item availability across all selected stores.
      </Alert>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Operation
        </Typography>
        <Stack direction="row" spacing={1}>
          <Chip
            label="Snooze Item"
            onClick={() => onOperationChange('snooze')}
            color={itemsOperation === 'snooze' ? 'warning' : 'default'}
            variant={itemsOperation === 'snooze' ? 'filled' : 'outlined'}
          />
          <Chip
            label="Activate Item"
            onClick={() => onOperationChange('activate')}
            color={itemsOperation === 'activate' ? 'success' : 'default'}
            variant={itemsOperation === 'activate' ? 'filled' : 'outlined'}
          />
        </Stack>
      </Box>

      <Stack spacing={2}>
        {/* Type Selector */}
        <FormControl fullWidth>
          <InputLabel>Type</InputLabel>
          <Select
            value={itemType}
            onChange={(e) => {
              onItemTypeChange(e.target.value as typeof itemType);
              onSelectedItemsChange([]);
              setSearchQuery('');
              setSearchResults([]);
            }}
            label="Type"
          >
            <MenuItem value="PRODUCT">Product</MenuItem>
            <MenuItem value="BUNDLE">Bundle</MenuItem>
            <MenuItem value="VARIANT">Variant</MenuItem>
            <MenuItem value="MODIFIER">Modifier</MenuItem>
            <MenuItem value="OPTION_VALUE">Option Value</MenuItem>
          </Select>
        </FormControl>

        {/* Autocomplete Search - Multi-select */}
        <Autocomplete
          multiple
          value={selectedItems.map(item => ({
            code: item.code,
            name: item.name,
            label: `${item.code} - ${item.name}`
          }))}
          onChange={(_, newValue) => {
            const updatedItems = newValue.map(item => ({
              type: itemType,
              code: item.code,
              name: item.name
            }));
            onSelectedItemsChange(updatedItems);
          }}
          inputValue={searchQuery}
          onInputChange={(_, newInputValue, reason) => {
            if (reason === 'input') {
              setSearchQuery(newInputValue);
            }
          }}
          options={searchResults}
          getOptionLabel={(option) => option.label || `${option.code} - ${option.name}`}
          isOptionEqualToValue={(option, value) => option.code === value.code}
          disableCloseOnSelect
          loading={isSearching}
          loadingText="Searching..."
          noOptionsText={searchQuery.length < 2 ? `Type to search ${itemType}` : 'No results found'}
          renderInput={(params) => (
            <TextField
              {...params}
              label={`${itemType} Code *`}
              placeholder={selectedItems.length === 0 ? `Search for ${itemType.toLowerCase()}s...` : ''}
              required
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isSearching ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.code}
                label={option.name}
                size="small"
              />
            ))
          }
          renderOption={(props, option, { selected }) => {
            const { key, ...otherProps } = props as any;
            return (
              <Box component="li" key={key} {...otherProps}>
                <Checkbox
                  checked={selected}
                  size="small"
                  sx={{ mr: 1 }}
                />
                <Typography variant="body1">{option.label || `${option.code} - ${option.name}`}</Typography>
              </Box>
            );
          }}
        />

        {itemsOperation === 'snooze' && (
          <>
            {/* Reason Selector */}
            <FormControl fullWidth>
              <InputLabel>Reason</InputLabel>
              <Select
                value={itemReason}
                onChange={(e) => onItemReasonChange(e.target.value as typeof itemReason)}
                label="Reason"
              >
                <MenuItem value="OUT_OF_STOCK">Out of Stock</MenuItem>
                <MenuItem value="COMING_SOON">Coming Soon</MenuItem>
              </Select>
            </FormControl>

            {/* Quick Expiry Options */}
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Quick Options (Store Local Time)
              </Typography>
              <Stack spacing={1} sx={{ mt: 1, mb: 2 }}>
                {/* Hourly options */}
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {[1, 2, 4, 6, 12].map((hours) => (
                    <Chip
                      key={hours}
                      label={`${hours} H`}
                      onClick={() => {
                        const expiry = new Date();
                        expiry.setHours(expiry.getHours() + hours);
                        onItemExpiryChange(expiry);
                        setSelectedQuickOption(`${hours}h`);
                      }}
                      variant={selectedQuickOption === `${hours}h` ? 'filled' : 'outlined'}
                      color={selectedQuickOption === `${hours}h` ? 'primary' : 'default'}
                      size="small"
                    />
                  ))}
                </Stack>

                {/* Daily options */}
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  <Chip
                    label="End of Day (4 AM)"
                    onClick={() => {
                      onItemExpiryChange(getDefault4AM());
                      setSelectedQuickOption('endOfDay');
                    }}
                    variant={selectedQuickOption === 'endOfDay' ? 'filled' : 'outlined'}
                    color={selectedQuickOption === 'endOfDay' ? 'primary' : 'default'}
                    size="small"
                  />
                  <Chip
                    label="Tonight (11:59 PM)"
                    onClick={() => {
                      const tonight = new Date();
                      tonight.setHours(23, 59, 59, 999);
                      onItemExpiryChange(tonight);
                      setSelectedQuickOption('tonight');
                    }}
                    variant={selectedQuickOption === 'tonight' ? 'filled' : 'outlined'}
                    color={selectedQuickOption === 'tonight' ? 'primary' : 'default'}
                    size="small"
                  />
                  <Chip
                    label="Tomorrow"
                    onClick={() => {
                      const tomorrow = new Date();
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      tomorrow.setHours(new Date().getHours(), new Date().getMinutes(), 0, 0);
                      onItemExpiryChange(tomorrow);
                      setSelectedQuickOption('tomorrow');
                    }}
                    variant={selectedQuickOption === 'tomorrow' ? 'filled' : 'outlined'}
                    color={selectedQuickOption === 'tomorrow' ? 'primary' : 'default'}
                    size="small"
                  />
                  <Chip
                    label="1 Week"
                    onClick={() => {
                      const nextWeek = new Date();
                      nextWeek.setDate(nextWeek.getDate() + 7);
                      onItemExpiryChange(nextWeek);
                      setSelectedQuickOption('1week');
                    }}
                    variant={selectedQuickOption === '1week' ? 'filled' : 'outlined'}
                    color={selectedQuickOption === '1week' ? 'primary' : 'default'}
                    size="small"
                  />
                  <Chip
                    label="1 Month"
                    onClick={() => {
                      const nextMonth = new Date();
                      nextMonth.setMonth(nextMonth.getMonth() + 1);
                      onItemExpiryChange(nextMonth);
                      setSelectedQuickOption('1month');
                    }}
                    variant={selectedQuickOption === '1month' ? 'filled' : 'outlined'}
                    color={selectedQuickOption === '1month' ? 'primary' : 'default'}
                    size="small"
                  />
                </Stack>
              </Stack>
            </Box>

            {/* Expiry Date */}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label="Expires At *"
                value={itemExpiry}
                onChange={(newValue) => {
                  onItemExpiryChange(newValue);
                  setSelectedQuickOption('');
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: itemExpiry ? `Items reactivate: ${format(itemExpiry, 'MMM d, h:mm a')}` : 'Items will be automatically reactivated at this time',
                    required: true,
                  }
                }}
              />
            </LocalizationProvider>
          </>
        )}
      </Stack>
    </Box>
  );
};
